import React, { useState, useEffect, useRef } from "react";
import {
	Modal,
	ModalHeader,
	ModalBody,
	Button,
	Table,
	Spinner,
	FormGroup,
	Label,
} from "reactstrap";
import Select from "react-select";
import userService from "services/UserService";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import { ArrowClockwise, UserPlus, SignOut } from "@phosphor-icons/react";

const customStyles = {
	option: (provided, state) => ({
		...provided,
		color: state.isSelected ? "blue" : "black",
		backgroundColor: "white",
		"&:hover": {
			backgroundColor: "#556ee6",
		},
	}),
	control: (provided, state) => ({
		...provided,
		minHeight: "38px",
		borderColor: provided.borderColor,
		boxShadow: state.isFocused ? "0 0 0 1px #2684FF" : 0,
	}),
	menu: (provided) => ({
		...provided,
		zIndex: 9999,
	}),
};

const buttonStyle = {
	background: "none",
	padding: 10,
	cursor: "pointer",
	color: "black",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	gap: 8,
};

const InactiveUserConflictModal = ({
	isOpen,
	toggle,
	inactiveUsers = [],
	formValues,
	onReactivated,
	onReplaced,
}) => {
	const [selectedUserId, setSelectedUserId] = useState(null);
	const [report, setReport] = useState(null);
	const [loadingReport, setLoadingReport] = useState(false);
	const [actionLoading, setActionLoading] = useState(null); // 'reactivate' | 'replace'

	const list = Array.isArray(inactiveUsers) ? inactiveUsers : [];
	const options = list.map((u) => ({
		value: u.id,
		label: `${u.name || ""}${u.lastname ? ` (${u.lastname})` : ""}${u.email ? ` — ${u.email}` : ""} — ID ${u.id}`.trim(),
	}));
	const selectedUser = list.find((u) => u.id === selectedUserId) || list[0];
	const lastLoadedIdRef = useRef(null);

	useEffect(() => {
		if (!isOpen || list.length === 0) {
			setReport(null);
			setSelectedUserId(null);
			return;
		}
		setReport(null);
		setSelectedUserId(list[0]?.id ?? null);
	}, [isOpen, list.length]);

	useEffect(() => {
		if (!isOpen) lastLoadedIdRef.current = null;
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || list.length === 0) return;
		const id = selectedUserId ?? list[0]?.id;
		if (!id) return;
		if (lastLoadedIdRef.current === id) return;
		lastLoadedIdRef.current = id;
		let cancelled = false;
		setLoadingReport(true);
		userService
			.getInactiveReport(id)
			.then((data) => {
				if (!cancelled) setReport(data);
			})
			.catch((err) => {
				if (!cancelled) {
					SendaCustomSwal.fire({
						iconHtml: '<i class="error-icon"></i>',
						title: "Erro",
						text: err?.response?.data?.message || "Não foi possível carregar o relatório.",
						customClass: { confirmButton: "custom-confirm-button" },
					});
				}
			})
			.finally(() => {
				setLoadingReport(false);
			});
		return () => {
			cancelled = true;
			lastLoadedIdRef.current = null;
		};
	}, [isOpen, selectedUserId, list.length]);

	const handleUserChange = (option) => {
		setSelectedUserId(option?.value ?? null);
		setReport(null);
	};

	const handleReactivate = async () => {
		const id = selectedUserId ?? list[0]?.id;
		if (!id) return;
		setActionLoading("reactivate");
		try {
			await userService.reactivate(id);
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Usuário reativado com sucesso!",
				customClass: { confirmButton: "custom-confirm-button" },
			}).then(() => {
				toggle();
				if (onReactivated) onReactivated();
			});
		} catch (err) {
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Erro",
				text: err?.response?.data?.message || "Não foi possível reativar o usuário.",
				customClass: { confirmButton: "custom-confirm-button" },
			});
		} finally {
			setActionLoading(null);
		}
	};

	const handleCreateNew = async () => {
		if (!formValues) return;
		const id = selectedUserId ?? list[0]?.id;
		if (!id) return;
		setActionLoading("replace");
		try {
			await userService.replaceInactive({
				...formValues,
				replaceInactiveUserId: id,
			});
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Usuário criado com sucesso!",
				customClass: { confirmButton: "custom-confirm-button" },
			}).then(() => {
				toggle();
				if (onReplaced) onReplaced();
			});
		} catch (err) {
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Erro",
				text: err?.response?.data?.message || "Não foi possível criar o usuário.",
				customClass: { confirmButton: "custom-confirm-button" },
			});
		} finally {
			setActionLoading(null);
		}
	};

	return (
		<Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
			<div className="modal-content">
				<ModalHeader toggle={toggle}>
					{list.length > 1
						? "Usuários inativos com este e-mail"
						: "Usuário inativo com este e-mail"}
				</ModalHeader>
				<ModalBody className="px-4 py-5">
					<p className="text-muted font-size-16 mb-4">
						{list.length > 1
							? "Existem vários usuários inativos com este e-mail. Escolha um para ver o relatório, reativar ou substituir por um novo usuário."
							: "Já existe um usuário inativo com este e-mail. Você pode reativá-lo ou criar um novo usuário."}
					</p>
					{list.length > 1 && (
						<FormGroup className="mb-3">
							<Label>Selecionar usuário inativo</Label>
							<Select
								styles={customStyles}
								placeholder="Selecione..."
								value={options.find((o) => o.value === selectedUserId)}
								onChange={handleUserChange}
								options={options}
							/>
						</FormGroup>
					)}
					{loadingReport && (
						<div className="d-flex align-items-center gap-2 mb-3">
							<Spinner size="sm" /> Carregando relatório...
						</div>
					)}
					{report && (
						<div className="mb-4">
							<h6 className="mb-2">Dados do usuário inativo</h6>
							<p className="mb-1">
								<strong>Nome:</strong> {report.user?.name}
							</p>
							<p className="mb-1">
								<strong>Apelido:</strong> {report.user?.lastname ?? "-"}
							</p>
							<p className="mb-2">
								<strong>E-mail:</strong> {report.user?.email}
							</p>
							{report.companies?.length > 0 && (
								<>
									<h6 className="mb-2">Empresas e permissões</h6>
									<Table size="sm" bordered>
										<thead>
											<tr>
												<th>Empresa</th>
												<th>Permissão</th>
											</tr>
										</thead>
										<tbody>
											{report.companies.map((c, i) => (
												<tr key={i}>
													<td>
														{c?.name
															? c.name
															: "(empresa não existe mais no sistema)"}
													</td>
													<td>{c.permission ?? "-"}</td>
												</tr>
											))}
										</tbody>
									</Table>
								</>
							)}
						</div>
					)}
					<p className="text-muted small mb-3">
						<strong>Atenção:</strong> Criar um novo usuário irá excluir o usuário inativo selecionado (o e-mail dele será alterado para liberar o endereço).
					</p>
					<div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
						<div className="d-flex flex-wrap gap-2">
							<Button
								type="button"
								onClick={handleReactivate}
								disabled={!!actionLoading || !(selectedUserId ?? list[0]?.id)}
								style={buttonStyle}
							>
								{actionLoading === "reactivate" ? (
									<Spinner size="sm" />
								) : (
									<ArrowClockwise size={18} />
								)}
								Reativar usuário
							</Button>
							<Button
								type="button"
								onClick={handleCreateNew}
								disabled={!!actionLoading || !(selectedUserId ?? list[0]?.id)}
								style={buttonStyle}
							>
								{actionLoading === "replace" ? (
									<Spinner size="sm" />
								) : (
									<UserPlus size={18} />
								)}
								Criar novo usuário
							</Button>
						</div>
						<Button type="button" onClick={toggle} style={buttonStyle}>
							<SignOut size={16} /> Fechar
						</Button>
					</div>
				</ModalBody>
			</div>
		</Modal>
	);
};

export default InactiveUserConflictModal;
